import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Trash2, Plus, ImageIcon, ExternalLink, Loader2, Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_EVENT_IMAGES } from '../../utils/eventImages';

import { API_URL } from '../../utils/api';

export function AdminImagesTab() {
  const [customImages, setCustomImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomImages();
  }, []);

  const fetchCustomImages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/site-images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomImages(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddImage = async () => {
    if (!newImageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }

    // Basic URL validation
    if (!newImageUrl.startsWith('http://') && !newImageUrl.startsWith('https://')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setAdding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/site-images/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_url: newImageUrl.trim() })
      });

      if (res.ok) {
        toast.success('Image added successfully');
        setNewImageUrl('');
        fetchCustomImages();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add image');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveImage = async (imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/site-images?image_url=${encodeURIComponent(imageUrl)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Image removed');
        fetchCustomImages();
      } else {
        toast.error('Failed to remove image');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const filteredDefaults = DEFAULT_EVENT_IMAGES.filter(url => 
    searchTerm ? url.toLowerCase().includes(searchTerm.toLowerCase()) : true
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Site Images</h2>
          <p className="text-slate-500 mt-1">
            Manage images used across the site. Custom images appear first in rotation.
          </p>
        </div>
      </div>

      {/* Add Custom Image */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-violet-600" />
          Add Custom Image
        </h3>
        <div className="flex gap-3">
          <Input
            placeholder="Paste image URL here (https://...)"
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            className="flex-1"
          />
          <Button onClick={handleAddImage} disabled={adding}>
            {adding ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Add Image
          </Button>
        </div>
        <p className="text-sm text-slate-400 mt-2">
          Tip: Use high-quality images from Unsplash or Pexels. Recommended size: 800x600px or larger.
        </p>
      </div>

      {/* Custom Images Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-emerald-600" />
          Custom Images ({customImages.length})
        </h3>
        
        {customImages.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No custom images added yet</p>
            <p className="text-sm">Add your own images above to personalize the site</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {customImages.map((url, index) => (
              <div key={index} className="relative group rounded-lg overflow-hidden border border-slate-200">
                <img
                  src={url}
                  alt={`Custom ${index + 1}`}
                  className="w-full h-32 object-cover"
                  onError={(e) => { e.target.src = '/placeholder.svg'; }}
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white rounded-full hover:bg-slate-100"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-700" />
                  </a>
                  <button
                    onClick={() => handleRemoveImage(url)}
                    className="p-2 bg-red-500 rounded-full hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="absolute top-2 left-2">
                  <span className="bg-emerald-500 text-white text-xs px-2 py-1 rounded-full">Custom</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Default Images Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-violet-600" />
            Default Stock Images ({DEFAULT_EVENT_IMAGES.length})
          </h3>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <p className="text-sm text-slate-500 mb-4">
          These are the built-in stock images. They are used when no custom images are added or as fallback.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredDefaults.map((url, index) => (
            <div key={index} className="relative group rounded-lg overflow-hidden border border-slate-200">
              <img
                src={url}
                alt={`Default ${index + 1}`}
                className="w-full h-32 object-cover"
                onError={(e) => { e.target.src = '/placeholder.svg'; }}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-white rounded-full hover:bg-slate-100"
                >
                  <ExternalLink className="w-4 h-4 text-slate-700" />
                </a>
              </div>
              <div className="absolute top-2 left-2">
                <span className="bg-slate-500 text-white text-xs px-2 py-1 rounded-full">Stock</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-violet-50 rounded-xl p-4 border border-violet-200">
        <div className="flex items-center gap-3">
          <Check className="w-5 h-5 text-violet-600" />
          <div>
            <p className="font-medium text-violet-900">
              Total: {customImages.length + DEFAULT_EVENT_IMAGES.length} images available
            </p>
            <p className="text-sm text-violet-700">
              {customImages.length} custom + {DEFAULT_EVENT_IMAGES.length} stock images
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminImagesTab;
